<div class="row mb-3">
  <div class="col-12">
    <h2>{{ isEditMode ? 'Edit POS Terminal' : 'Create POS Terminal' }}</h2>
  </div>
</div>

<div class="col-12">
  <nb-card>
    <nb-card-body>
      <form [formGroup]="terminalForm" (ngSubmit)="onSubmit()">

        <!-- Terminal Name -->
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Terminal Name <span class="text-danger">*</span></label>
            <input
              type="text"
              class="form-control"
              formControlName="terminalName"
              placeholder="e.g. Checkout 1">
            <div *ngIf="terminalForm.get('terminalName')?.invalid && terminalForm.get('terminalName')?.touched"
                 class="text-danger small">
              Terminal name is required
            </div>
          </div>
          <div class="col-md-3">
            <label class="form-label">Auto-Logout (minutes)</label>
            <input type="number" class="form-control" formControlName="autoLogoutMinutes" min="1">
          </div>
          <div class="col-md-6">
            <label class="form-label">Assigned login</label>
            <select class="form-select" formControlName="assignedUserId">
              <option [ngValue]="null">Not assigned</option>
              <option
                *ngFor="let user of assignableUsers"
                [ngValue]="user._id"
                [disabled]="isUserOptionDisabled(user)">
                {{ userOptionLabel(user) }}
              </option>
            </select>
            <small class="text-muted">
              Only managers can be assigned. The assigned manager logs straight into this terminal; other logins cannot use it.
            </small>
          </div>
        </div>

        <hr>

        <!-- Scanner & Payment -->
        <div class="row mb-3">
          <div class="col-md-4">
            <label class="form-label">Preferred Scanner</label>
            <select class="form-select" formControlName="preferredScannerType">
              <option value="camera">Camera</option>
              <option value="external">External Scanner</option>
            </select>
            <label class="form-label mt-2">QR codes when scanning products</label>
            <select class="form-select" formControlName="productScanQrMode">
              <option value="scan">Scan QR as barcode</option>
              <option value="warn">Warn</option>
              <option value="ignore">Do nothing</option>
            </select>
            <small class="text-muted">Applies to product scanning only (sale, price tags, add product). Customer login QR codes are not affected.</small>
          </div>
          <div class="col-md-4">
            <label class="form-label">Preferred Payment Method</label>
            <select class="form-select" formControlName="preferredPaymentMethod">
              <option value="customerDevice">Customer Device</option>
              <option value="cardTerminal">Card Terminal</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Age Verification Method</label>
            <select class="form-select" formControlName="ageVerificationMethod">
              <option value="hit2open">QR Code (Hit2Open App)</option>
              <option value="ageproof">QR Code (AgeProof)</option>
              <option value="both">Both (QR + NFC Card)</option>
              <option value="none">Disabled (skip age verification)</option>
            </select>
            <small class="text-muted">NFC Reader is a separate hardware option. Use "Disabled" only to skip age verification entirely.</small>
          </div>
        </div>

        <!-- Card Terminal Fields -->
        <div *ngIf="showCardTerminalFields" class="row mb-3 border rounded p-3 mx-0 bg-light">
          <div class="col-12 mb-2"><strong>Card Terminal Settings</strong></div>

          <!-- Cloud Terminal API toggle -->
          <div class="col-12 mb-3 d-flex align-items-center gap-3">
            <label class="form-label mb-0">Use Cloud Terminal API</label>
            <nb-toggle formControlName="useCloudTerminalApi"></nb-toggle>
            <small class="text-muted">Enable to connect via Viva Wallet Cloud Terminal API instead of LAN / proxy</small>
          </div>

          <!-- LAN / proxy fields (shown when Cloud Terminal API is OFF) -->
          <ng-container *ngIf="!useCloudTerminalApi">
            <div class="col-md-3">
              <label class="form-label">Terminal IP</label>
              <input type="text" class="form-control" formControlName="cardTerminalIP" placeholder="192.168.1.100">
            </div>
            <div class="col-md-2">
              <label class="form-label">Port</label>
              <input type="number" class="form-control" formControlName="cardTerminalPort">
            </div>
            <div class="col-md-2">
              <label class="form-label">Protocol</label>
              <select class="form-select" formControlName="cardTerminalProtocol">
                <option value="http">http</option>
                <option value="https">https</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label">POS ID</label>
              <input type="text" class="form-control" formControlName="cardTerminalPosId" placeholder="terminal-001">
            </div>
          </ng-container>

          <!-- Cloud Terminal API fields (shown when Cloud Terminal API is ON) -->
          <ng-container *ngIf="useCloudTerminalApi">
            <div class="col-md-3">
              <label class="form-label">Viva Terminal ID</label>
              <input type="text" class="form-control" formControlName="vivaTerminalId" placeholder="e.g. 16015332">
              <small class="text-muted">Terminal ID from the Viva Wallet dashboard</small>
            </div>
            <div class="col-md-4">
              <label class="form-label">POS Merchant ID</label>
              <input type="text" class="form-control" formControlName="vivaMerchantIdPOS" placeholder="Merchant ID for POS API Basic Auth">
            </div>
            <div class="col-md-4">
              <label class="form-label">POS API Key</label>
              <input type="password" class="form-control" formControlName="vivaApiKeyPOS" placeholder="API Key for POS API Basic Auth" autocomplete="new-password">
            </div>
          </ng-container>
        </div>

        <hr>

        <!-- Toggles Row 1 -->
        <div class="row mb-3">
          <div class="col-md-2 d-flex flex-column align-items-start">
            <label class="form-label">Sound</label>
            <nb-toggle formControlName="soundEnabled"></nb-toggle>
          </div>
          <div class="col-md-2 d-flex flex-column align-items-start">
            <label class="form-label">Haptic</label>
            <nb-toggle formControlName="hapticEnabled"></nb-toggle>
          </div>
          <div class="col-md-2 d-flex flex-column align-items-start">
            <label class="form-label">Guest Checkout</label>
            <nb-toggle formControlName="guestCheckoutEnabled"></nb-toggle>
          </div>
          <div class="col-md-3">
            <label class="form-label">Guest Inactivity (minutes)</label>
            <input
              type="number"
              class="form-control"
              formControlName="guestInactivityMinutes"
              min="1"
              max="60"
              step="1">
            <small class="text-muted">Auto-clear guest basket after inactivity (1–60 min, default 5)</small>
            <div
              *ngIf="terminalForm.get('guestInactivityMinutes')?.invalid && terminalForm.get('guestInactivityMinutes')?.touched"
              class="text-danger small">
              Enter an integer between 1 and 60
            </div>
          </div>
          <div class="col-md-2 d-flex flex-column align-items-start">
            <label class="form-label">Receipt Printer</label>
            <nb-toggle formControlName="receiptPrinterEnabled"></nb-toggle>
          </div>
          <div class="col-md-2 d-flex flex-column align-items-start" *ngIf="showPrinterFields">
            <label class="form-label">Auto-Print Receipt</label>
            <nb-toggle formControlName="autoPrintReceiptEnabled"></nb-toggle>
          </div>
          <div class="col-md-2 d-flex flex-column align-items-start">
            <label class="form-label">NFC Reader</label>
            <nb-toggle formControlName="nfcReaderEnabled"></nb-toggle>
          </div>
        </div>

        <!-- Printer IP -->
        <div *ngIf="showPrinterFields" class="row mb-3 border rounded p-3 mx-0 bg-light">
          <div class="col-12 mb-2"><strong>Printer Settings</strong></div>
          <div class="col-md-4">
            <label class="form-label">Printer IP</label>
            <input type="text" class="form-control" formControlName="printerIP" placeholder="192.168.1.50">
          </div>
        </div>

        <!-- NFC Mode -->
        <div *ngIf="showNfcModeField" class="row mb-3">
          <div class="col-md-3">
            <label class="form-label">NFC Reader Mode</label>
            <select class="form-select" formControlName="nfcReaderMode">
              <option value="hid">HID</option>
              <option value="webusb">WebUSB</option>
            </select>
          </div>
        </div>

        <hr>

        <!-- Proxy Toggle (LAN mode only) -->
        <div class="row mb-3" *ngIf="!showCardTerminalFields || !useCloudTerminalApi">
          <div class="col-md-2 d-flex flex-column align-items-start">
            <label class="form-label">Use Proxy</label>
            <nb-toggle formControlName="useProxy"></nb-toggle>
          </div>
        </div>

        <!-- Proxy Fields -->
        <div *ngIf="showProxyFields && (!showCardTerminalFields || !useCloudTerminalApi)" class="row mb-3 border rounded p-3 mx-0 bg-light">
          <div class="col-12 mb-2"><strong>Proxy Settings</strong></div>
          <div class="col-md-3">
            <label class="form-label">Protocol</label>
            <select class="form-select" formControlName="proxyProtocol">
              <option value="http">http</option>
              <option value="https">https</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Proxy IP</label>
            <input type="text" class="form-control" formControlName="proxyIP" placeholder="192.168.1.1">
          </div>
          <div class="col-md-2">
            <label class="form-label">Port</label>
            <input type="number" class="form-control" formControlName="proxyPort">
          </div>
        </div>

        <!-- APG Smarttill -->
        <div class="row mb-3">
          <div class="col-md-4 d-flex flex-column align-items-start">
            <label class="form-label">APG Smarttill (cash drawer)</label>
            <nb-toggle formControlName="smartTillEnabled"></nb-toggle>
            <small class="text-muted mt-1">
              Only enable on terminals with an APG Smarttill connected to the POS PC (Sommerbyen pilot).
              Requires Hit2Open Proxy v4.3.0+ and the APG SmarttillAPI service on the same PC.
            </small>
          </div>
        </div>

        <div *ngIf="showSmartTillFields" class="row mb-3 border rounded p-3 mx-0 bg-light">
          <div class="col-12 mb-2"><strong>APG Smarttill Settings</strong></div>
          <div class="col-md-3">
            <label class="form-label">Proxy host</label>
            <input type="text" class="form-control" formControlName="smartTillProxyHost" placeholder="127.0.0.1">
            <small class="text-muted">The Hit2Open Proxy only listens on this PC — use 127.0.0.1</small>
            <div
              *ngIf="terminalForm.get('smartTillProxyHost')?.invalid && terminalForm.get('smartTillProxyHost')?.touched"
              class="text-danger small">
              Proxy host is required
            </div>
          </div>
          <div class="col-md-2">
            <label class="form-label">Proxy port</label>
            <input type="number" class="form-control" formControlName="smartTillProxyPort" min="1" max="65535">
            <div
              *ngIf="terminalForm.get('smartTillProxyPort')?.invalid && terminalForm.get('smartTillProxyPort')?.touched"
              class="text-danger small">
              Enter a port between 1 and 65535
            </div>
          </div>
          <div class="col-md-3">
            <label class="form-label">Till number</label>
            <input type="text" class="form-control" formControlName="smartTillTillNo" placeholder="123456" maxlength="6">
            <small class="text-muted">Exactly 6 digits</small>
            <div
              *ngIf="terminalForm.get('smartTillTillNo')?.invalid && terminalForm.get('smartTillTillNo')?.touched"
              class="text-danger small">
              Till number must be exactly 6 digits
            </div>
          </div>
          <div class="col-md-3">
            <label class="form-label">Cashier number</label>
            <input type="text" class="form-control" formControlName="smartTillCashierNo" placeholder="12345678" maxlength="8">
            <small class="text-muted">Exactly 8 digits</small>
            <div
              *ngIf="terminalForm.get('smartTillCashierNo')?.invalid && terminalForm.get('smartTillCashierNo')?.touched"
              class="text-danger small">
              Cashier number must be exactly 8 digits
            </div>
          </div>
          <div class="col-md-4 d-flex flex-column align-items-start mt-2">
            <label class="form-label">Require Smarttill for cash payments</label>
            <nb-toggle formControlName="smartTillRequireForCashPay"></nb-toggle>
          </div>
        </div>

        <!-- Max Card Payment Amount -->
        <div class="row mb-3">
          <div class="col-md-4">
            <label class="form-label">Max card payment amount (DKK)</label>
            <input type="number" class="form-control" formControlName="maxCardPaymentAmount" min="0" placeholder="No limit">
            <small class="text-muted">Leave blank for no limit</small>
          </div>
        </div>

        <hr>

        <!-- Actions -->
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" (click)="goBack()">Cancel</button>
          <button type="submit" class="btn btn-primary">{{ isEditMode ? 'Save Changes' : 'Create Terminal' }}</button>
        </div>

      </form>
    </nb-card-body>
  </nb-card>
</div>

<ngx-spinner bdColor="rgba(51,51,51,0.8)" size="large" [fullScreen]="true" color="white" type="ball-circus">
  <p style="color: white">Loading...</p>
</ngx-spinner>
